import { useStyles } from '@archipro-design/aria';
import React from 'react';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import ColorPicker from './ColorPicker';
import OptionPicker from './OptionPicker';
import SizePicker from './SizePicker';
import * as S from './VariantSelector.style';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import type Variant from '@modules/product/type/variant';
import type ProductSize from '@modules/product/type/product-size';

interface VariantSelectorProps {
    data: {
        variants: Variant[];
        sizes: ProductSize[];
    };
}

const VariantSelector = ({
    data: { variants, sizes },
}: VariantSelectorProps) => {
    const { attributeMap } = useVariantOptionState();
    const colourAttribute = attributeMap.get(ProductAttributeEnum.Colour);
    const materialAttribute = attributeMap.get(ProductAttributeEnum.Material);
    const otherOptions = [...attributeMap.values()].filter(
        (attr) =>
            attr.ID !== ProductAttributeEnum.Colour &&
            attr.ID !== ProductAttributeEnum.Material
    );
    const { css } = useStyles();

    const hasOptions =
        colourAttribute ||
        materialAttribute ||
        sizes.length > 0 ||
        otherOptions.length > 0;

    return hasOptions ? (
        <div className={css(S.VariantSelector)}>
            {colourAttribute && (
                <ColorPicker
                    key={'option-picker' + ProductAttributeEnum.Colour}
                    data={{
                        variants,
                        options: colourAttribute.options,
                    }}
                    isGeneric={colourAttribute.isGeneric}
                />
            )}
            {materialAttribute && (
                <OptionPicker
                    key={'option-picker' + ProductAttributeEnum.Material}
                    data={{
                        attributeID: ProductAttributeEnum.Material,
                        label: materialAttribute.Name,
                        variants,
                        options: materialAttribute.options,
                    }}
                    isGeneric={materialAttribute.isGeneric}
                />
            )}
            {sizes.length > 0 && (
                <SizePicker
                    data={{
                        variants,
                        sizes,
                    }}
                    isGeneric={variants.every((v) => Boolean(v.Size))}
                />
            )}
            {otherOptions.map((attr) => (
                <OptionPicker
                    key={'option-picker' + attr.ID}
                    data={{
                        attributeID: attr.ID,
                        label: attr.Name,
                        variants: variants,
                        options: attr.options,
                    }}
                    isGeneric={attr.isGeneric}
                />
            ))}
        </div>
    ) : null;
};

export default VariantSelector;
